import { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, Button, message, Tag, Modal, Select, Switch, Slider, Space, Descriptions, Row, Col, Empty } from 'antd';
import { SafetyCertificateOutlined, IdcardOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getUser } from '../../api';
import { sealCanvas } from '../../utils/sealCanvas';

const token = () => localStorage.getItem('ar_token');
const h = () => ({ Authorization: 'Bearer ' + token() });

// 出库章（CompanySeal）图片接口
const sealImgUrl = (id) => `/api/asign/stamp/image/${id}?token=${encodeURIComponent(token() || '')}`;

export default function SupplierAuth() {
  const user = getUser();

  // ===== 企业认证状态 =====
  const [companyInfo, setCompanyInfo] = useState({ company_name: '', credit_code: '' });
  const [enterpriseStatus, setEnterpriseStatus] = useState('none'); // none | pending | submitted | passed | rejected
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certForm] = Form.useForm();
  const [certLoading, setCertLoading] = useState(false);

  // ===== 出库章制作状态 =====
  const [sealModalOpen, setSealModalOpen] = useState(false);
  const [stampFields, setStampFields] = useState({
    type: 'company',
    style: 'oval',
    stampText: '',
    topText: '',
    bottomText: '',
    centerText: '出库专用章',
    showStar: true,
    centerFontSize: 0,
    topFontSize: 0,
    bottomFontSize: 0,
    aged: false,
  });
  const [stampPreviewSize, setStampPreviewSize] = useState({ w: 200, h: 136 });
  const [stampLoading, setStampLoading] = useState(false);
  const [mySealList, setMySealList] = useState([]);
  const [editingSealId, setEditingSealId] = useState(null);
  const stampCanvasRef = useRef(null);

  const loadAll = () => {
    axios.get('/api/customer/company-info', { headers: h() }).then(r => {
      if (r.data.success) setCompanyInfo({ company_name: r.data.company_name || user?.ccusname || '', credit_code: r.data.credit_code || '' });
    }).catch(() => {});
    axios.get('/api/customer/auth-status', { headers: h() }).then(r => {
      if (r.data.success) setEnterpriseStatus(r.data.enterprise_status || 'none');
    }).catch(() => {});
    fetchMySealList();
  };

  useEffect(() => { loadAll(); }, []);

  // ===== 企业认证 =====
  const startEnterpriseAuth = () => {
    certForm.setFieldsValue({ company_name: companyInfo.company_name, credit_code: companyInfo.credit_code });
    setCertModalOpen(true);
  };

  const handleCertSubmit = async () => {
    try {
      const vals = await certForm.validateFields();
      setCertLoading(true);
      const r = await axios.post('/api/customer/company-identify', vals, { headers: h() });
      if (r.data.success && r.data.identify_url) {
        setCertModalOpen(false);
        certForm.resetFields();
        const certWindow = window.open(r.data.identify_url, 'aisign_cert');
        const newSerialNo = r.data.serial_no || '';
        message.info('认证页面已打开，完成后将自动检测并关闭');
        setEnterpriseStatus('pending');
        let pollCount = 0;
        const pollInterval = setInterval(async () => {
          pollCount++;
          if (pollCount > 120) { clearInterval(pollInterval); return; }
          if (certWindow && certWindow.closed) { clearInterval(pollInterval); return; }
          try {
            const cr = await axios.post('/api/customer/company-cert-done', { serial_no: newSerialNo }, { headers: h() });
            if (cr.data.success && cr.data.passed) {
              clearInterval(pollInterval);
              message.success('企业认证已通过！认证窗口已自动关闭');
              try { certWindow && certWindow.close(); } catch {}
              setEnterpriseStatus('passed');
              loadAll();
            }
          } catch { /* 忽略轮询异常 */ }
        }, 5000);
        loadAll();
      } else {
        message.error(r.data.message || '获取认证链接失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      const msg = e.response?.data?.detail || e.response?.data?.message || '请求失败';
      if (typeof msg === 'string') message.error(msg); else message.error('获取认证链接失败');
    }
    setCertLoading(false);
  };

  // ===== 出库章制作 =====
  const fetchMySealList = async () => {
    try {
      const res = await axios.get('/api/customer/seals', { headers: h() });
      setMySealList(res.data.data || []);
    } catch (e) { /* ignore */ }
  };

  const openSealModal = (existingSeal = null) => {
    if (existingSeal) {
      setEditingSealId(existingSeal.id);
      setStampFields({
        type: 'company', style: 'oval',
        stampText: existingSeal.seal_title || existingSeal.seal_name || '',
        topText: companyInfo.company_name || user?.ccusname || '',
        bottomText: '', centerText: '出库专用章',
        showStar: true, centerFontSize: 0, topFontSize: 0, bottomFontSize: 0, aged: false,
      });
    } else {
      setEditingSealId(null);
      setStampFields({
        type: 'company', style: 'oval',
        stampText: (companyInfo.company_name || user?.ccusname || '') + '出库专用章',
        topText: companyInfo.company_name || user?.ccusname || '',
        bottomText: '', centerText: '出库专用章',
        showStar: true, centerFontSize: 0, topFontSize: 0, bottomFontSize: 0, aged: false,
      });
    }
    setStampPreviewSize({ w: 200, h: 136 });
    setSealModalOpen(true);
  };

  const makeCompanySeal = async () => {
    if (!stampFields.topText) return message.error('请填写顶部文字（公司名称）');
    if (!stampFields.centerText) return message.error('请填写中心文字');
    setStampLoading(true);
    try {
      const canvas = stampCanvasRef.current;
      let image_base64 = '';
      if (canvas) {
        image_base64 = canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
      } else {
        message.error('Canvas 未就绪，请重试');
        setStampLoading(false);
        return;
      }
      const payload = {
        image_base64,
        stamp_text: stampFields.centerText,
        top_text: stampFields.topText,
        bottom_text: stampFields.bottomText,
        seal_title: stampFields.stampText,
        style: stampFields.style,
        show_star: stampFields.showStar,
        aged: stampFields.aged ?? false,
        top_font_size: stampFields.topFontSize || 0,
        center_font_size: stampFields.centerFontSize || 0,
        bottom_font_size: stampFields.bottomFontSize || 0,
        seal_id: editingSealId || null,
      };
      const res = await axios.post('/api/customer/upload-stamp-image', payload, { headers: h() });
      if (res.data.success) {
        message.success(res.data.message || '出库章已保存');
        setSealModalOpen(false);
        setEditingSealId(null);
        fetchMySealList();
      } else {
        message.error(res.data.message || '操作失败');
      }
    } catch (e) {
      message.error('操作失败: ' + (e.response?.data?.message || e.message));
    }
    setStampLoading(false);
  };

  const statusTag = (status) => {
    const map = {
      none: { color: 'default', text: '未认证' },
      pending: { color: 'processing', text: '认证中' },
      submitted: { color: 'warning', text: '待审核' },
      passed: { color: 'success', text: '已通过' },
      rejected: { color: 'error', text: '已驳回' },
    };
    const m = map[status] || map.none;
    return <Tag color={m.color}>{m.text}</Tag>;
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <Card
        title={<Space><SafetyCertificateOutlined />三方认证</Space>}
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadAll}>刷新</Button>}
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={2} size="small">
          <Descriptions.Item label="企业名称">{companyInfo.company_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="统一社会信用代码">{companyInfo.credit_code || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={16}>
        {/* ===== 企业认证 ===== */}
        <Col xs={24} md={12}>
          <Card
            title={<Space><IdcardOutlined />企业认证</Space>}
            extra={statusTag(enterpriseStatus)}
            style={{ marginBottom: 16, minHeight: 220 }}
          >
            <div style={{ color: '#666', marginBottom: 16 }}>
              {enterpriseStatus === 'passed'
                ? '企业已完成认证，如需重新认证请联系管理员。'
                : '通过爱签完成企业实名认证，认证通过后可制作出库章。'}
            </div>
            <Button type="primary" block
              disabled={enterpriseStatus === 'passed'}
              loading={certLoading}
              onClick={startEnterpriseAuth}>
              {enterpriseStatus === 'passed' ? '已通过认证' : enterpriseStatus === 'pending' ? '认证进行中' : '去企业认证'}
            </Button>
          </Card>
        </Col>

        {/* ===== 出库章制作 ===== */}
        <Col xs={24} md={12}>
          <Card
            title={<Space><EditOutlined />出库章制作</Space>}
            extra={<Tag color="blue">出库专用章</Tag>}
            style={{ marginBottom: 16, minHeight: 220 }}
          >
            {mySealList.length > 0 ? (
              <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                {mySealList.map(s => (
                  <img key={s.id} src={sealImgUrl(s.id)}
                    style={{ width: 96, height: 96, border: '1px solid #ddd', borderRadius: 4, objectFit: 'contain' }}
                    onClick={() => window.open(sealImgUrl(s.id))} />
                ))}
              </div>
            ) : (
              <Empty description="暂无出库章" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '8px 0 16px' }} />
            )}
            <Button type="primary" ghost block onClick={() => openSealModal(mySealList[0] || null)}>
              {mySealList.length > 0 ? '修改出库章' : '出库章制作'}
            </Button>
          </Card>
        </Col>
      </Row>

      {/* ===== 企业认证弹窗 ===== */}
      <Modal title="企业认证" open={certModalOpen} onCancel={() => setCertModalOpen(false)}
        footer={null} destroyOnClose>
        <Form form={certForm} layout="vertical" onFinish={handleCertSubmit}>
          <Form.Item label="企业名称" name="company_name" rules={[{ required: true, message: '请输入企业名称' }]}>
            <Input placeholder="请输入企业名称" />
          </Form.Item>
          <Form.Item label="统一社会信用代码" name="credit_code" rules={[{ required: true, message: '请输入统一社会信用代码' }]}>
            <Input placeholder="请输入统一社会信用代码" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={certLoading}>提交认证</Button>
        </Form>
      </Modal>

      {/* ===== 出库章制作弹窗 ===== */}
      <Modal title={editingSealId ? '修改出库章' : '出库章制作'} open={sealModalOpen}
        onCancel={() => setSealModalOpen(false)} footer={null} width={720} destroyOnClose>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <Form layout="vertical">
              <Form.Item label="印章名称（章的名字）">
                <Input value={stampFields.stampText}
                  onChange={e => setStampFields(f => ({ ...f, stampText: e.target.value }))} />
              </Form.Item>
              <Form.Item label="顶部文字（公司名称）">
                <Input value={stampFields.topText} disabled />
              </Form.Item>
              <Form.Item label="印章样式">
                <Select value={stampFields.style}
                  onChange={v => { setStampFields(f => ({ ...f, style: v })); setStampPreviewSize(v === 'oval' ? { w: 200, h: 136 } : { w: 200, h: 200 }); }}
                  options={[{ value: 'oval', label: '椭圆章' }, { value: 'round', label: '圆形章' }]} />
              </Form.Item>
              <Form.Item label="中心文字">
                <Select value={stampFields.centerText}
                  onChange={v => setStampFields(f => ({ ...f, centerText: v }))}
                  options={[
                    { value: '出库专用章', label: '出库专用章' },
                    { value: '发货专用章', label: '发货专用章' },
                  ]} />
              </Form.Item>
              <Form.Item label="中心文字字号">
                <Slider min={0} max={36} value={stampFields.centerFontSize}
                  onChange={v => setStampFields(f => ({ ...f, centerFontSize: v }))} />
                <div style={{ fontSize: 12, color: '#999' }}>0=自动</div>
              </Form.Item>
              <Form.Item label="显示五角星">
                <Switch checked={stampFields.showStar}
                  onChange={v => setStampFields(f => ({ ...f, showStar: v }))} />
              </Form.Item>
              <Button type="primary" block loading={stampLoading} onClick={makeCompanySeal}>
                {editingSealId ? '确认修改' : '提交制作'}
              </Button>
            </Form>
          </div>
          <div style={{ width: `min(${stampPreviewSize.w}px, 30vw)`, flexShrink: 0 }}>
            <canvas ref={el => {
              stampCanvasRef.current = el;
              if (el) { el.width = stampPreviewSize.w; el.height = stampPreviewSize.h; sealCanvas(el, stampFields); }
            }} style={{ width: '100%', height: 'auto' }} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
